import { Web, Edit, NodeId, EditState, UIState } from 'web';
import { CompositorNodeV3 } from '@/types/nodes/compositor';
import { log } from '@/logger/logger.ts';
import { FlowStore } from './FlowStore';
import { UndoRedoEntry } from './undoRedo';
import type { LogLevel } from 'loglevel';
import type { FlowUIState } from './FlowGraph';

export type WasmErrorType = 'fatal' | 'recovered' | 'webgl-not-supported' | 'context-lost';

export class WasmAPI {
  private canvas: HTMLCanvasElement;
  private wasm: Web | undefined = undefined;
  private store: FlowStore;
  private requestRef: number;
  private editable: boolean;
  private onError: (errorType: WasmErrorType) => void;
  private updateUIState: (updater: (oldState: FlowUIState) => Partial<FlowUIState>) => void;
  private updateLegacy: (nodeId: NodeId, isSeek: boolean) => void;
  private pushUndoState: (entry: UndoRedoEntry) => void;
  private lastCreationTime: number | undefined = undefined;

  private log = (level: keyof LogLevel, message: string, e?: unknown) => {
    let input: any[] = [message];
    if (e instanceof Error) {
      input = [
        message + ': ' + e.message,
        e,
        {
          name: e.name,
          errorMessage: e.message,
          stack: e.stack,
        },
      ];
    } else if (typeof e === 'string') {
      input = [message + ': ' + e];
    }
    const logger = log.getLogger('Wasm');
    switch (level) {
      case 'ERROR':
        logger.error(...input);
        break;
      case 'WARN':
        logger.warn(...input);
        break;
      case 'INFO':
        logger.info(...input);
        break;
      case 'DEBUG':
        logger.debug(...input);
        break;
      case 'TRACE':
        logger.trace(...input);
        break;
    }
  };

  private animate = () => {
    try {
      this.call((wasm) => {
        wasm.update();
        return [];
      });
    } catch (e) {
      this.wasm = undefined;
      this.log('ERROR', 'Animation loop failure - Unknown error in Wasm', e);
      this.onError('fatal');
    }
    this.requestRef = requestAnimationFrame(this.animate);
  };

  constructor(
    canvas: HTMLCanvasElement,
    store: FlowStore,
    editable: boolean,
    {
      onError,
      updateUIState,
      pushUndoState,
      updateLegacy,
    }: {
      onError: (errorType: WasmErrorType) => void;
      updateUIState: (updater: (oldState: FlowUIState) => Partial<FlowUIState>) => void;
      pushUndoState: (entry: UndoRedoEntry) => void;
      updateLegacy: (nodeId: NodeId, isSeek: boolean) => void;
    },
  ) {
    this.store = store;
    this.canvas = canvas;
    this.editable = editable;
    this.onError = onError;
    this.pushUndoState = pushUndoState;
    this.updateUIState = updateUIState;
    this.updateLegacy = updateLegacy;
    this.canvas.addEventListener('webglcontextlost', this.onContextLost, false);
    this.canvas.addEventListener('webglcontextrestored', this.onContextRestored, false);

    this.requestRef = requestAnimationFrame(this.animate);

    this.createWasm();
  }

  private createWasm() {
    this.wasm = undefined;
    try {
      this.wasm = new Web(
        (nodeId: NodeId, index: number, uiState: UIState) => {
          switch (uiState.type) {
            case 'compositor': {
              this.updateUIState((s) => {
                return { compositor: { ...s.compositor, [nodeId]: uiState.data } };
              });
              break;
            }
            case 'video': {
              this.updateUIState((s) => {
                const array = [...(s.video[nodeId] ?? [])];
                array[index] = uiState.data;
                return { video: { ...s.video, [nodeId]: array } };
              });
              break;
            }
            case 'painter': {
              this.updateUIState((s) => {
                return { painter: { ...s.painter, [nodeId]: uiState.data } };
              });
              break;
            }
            case 'cursor': {
              this.updateUIState((s) => {
                return { cursor: { ...s.cursor, [nodeId]: uiState.data } };
              });
              break;
            }
            default: {
              const _exhaustiveCheck: never = uiState;
            }
          }
        },
        (edit: Edit) => {
          this.onEdit(edit.editState);
          this.pushUndoState(UndoRedoEntry.fromWasmEdit(edit.undoState));
        },
        this.updateLegacy,
        this.canvas,
        this.editable,
      );
      this.lastCreationTime = performance.now();
    } catch (e) {
      if (typeof e === 'string' && e.includes('NotSupported')) {
        // WebGL not supported on user hardware, not possible to recover
        this.log('INFO', 'Failed to create WebGL2 context', e);
        this.onError('webgl-not-supported');
      } else if (e instanceof WebAssembly.RuntimeError) {
        this.log('ERROR', 'Failed to create Wasm instance - Panic in Wasm', e);
        this.onError('fatal');
      } else {
        this.log('ERROR', 'Failed to create Wasm instance - Unknown error in Wasm', e);
        this.onError('fatal');
      }
    }
  }

  private tryRecover = (error: WebAssembly.RuntimeError) => {
    // Test that we don't get an error immediately after recovering
    if (this.lastCreationTime && performance.now() - this.lastCreationTime < 2000) {
      this.wasm = undefined;
      this.log('ERROR', 'Failed to recover after panic, immidiate error', error);
      this.onError('fatal');
      return;
    }

    this.createWasm();
    if (this.wasm !== undefined) {
      this.log('ERROR', 'Succeeded to recover after panic', error);
      this.onError('recovered');
    } else {
      this.log('ERROR', 'Failed to recover after panic', error);
    }
  };

  call<R>(apply: (wasm: Web) => R) {
    try {
      if (this.wasm !== undefined) {
        return apply(this.wasm);
      }
    } catch (e) {
      if (e instanceof WebAssembly.RuntimeError) {
        this.tryRecover(e);
      } else {
        throw e;
      }
    }
  }

  edit(apply: (wasm: Web) => Edit): EditState[] | undefined {
    try {
      if (this.wasm !== undefined) {
        const editState = apply(this.wasm);
        this.onEdit(editState.editState);
        return editState.undoState;
      }
    } catch (e) {
      if (e instanceof WebAssembly.RuntimeError) {
        this.tryRecover(e);
      } else {
        throw e;
      }
    }
  }

  private onEdit(editStates: EditState[]) {
    for (const editState of editStates) {
      switch (editState.type) {
        case 'update_layer': {
          const { nodeId, layerId, layer } = editState;
          this.store.updateNodeData<CompositorNodeV3>(nodeId, (prevNode) => {
            const compositorData = prevNode.data;
            return {
              data: {
                ...compositorData,
                layers: Object.fromEntries(
                  Object.entries(compositorData.layers).map(([id, prevLayer]) => [
                    id,
                    id === String(layerId) ? layer : prevLayer,
                  ]),
                ),
              },
            };
          });
          break;
        }
        case 'update_node':
        case 'insert_node': {
          const { node, nodeId } = editState;
          if (node.kind.type === 'compv3') {
            const { kind: nodeKind, ...nodeProperties } = node;
            this.store.updateNode(nodeId, (prevNode) => {
              const prevNodeData = prevNode.data as CompositorNodeV3;
              return {
                ...nodeProperties,
                data: {
                  ...prevNode.data,
                  data: {
                    ...prevNodeData.data,
                    ...nodeKind.data,
                    layers: Object.fromEntries(
                      Array.from(nodeKind.data.layers).map(([layerId, layer]) => {
                        return [layerId, layer];
                      }),
                    ),
                  },
                },
              };
            });
          } else {
            this.store.updateNode(nodeId, (prevNode) => {
              const { kind: nodeKind, ...nodeProperties } = node;
              return { ...nodeProperties, data: { ...prevNode.data, ...nodeKind.data } };
            });
          }
          break;
        }
        case 'remove_node': {
          // TODO
          break;
        }
      }
    }
  }

  onContextLost = (e: Event) => {
    e.preventDefault();
    this.log('INFO', 'Context lost');
    this.wasm = undefined;
    this.onError('context-lost');
  };

  onContextRestored = (_e: Event) => {
    this.log('INFO', 'Context restored');

    this.createWasm();
    if (this.wasm !== undefined) {
      this.log('INFO', 'Successfully recovered from context lost');
      this.onError('recovered');
    } else {
      this.log('INFO', 'Failed to recover from context lost');
    }
  };

  dispose() {
    cancelAnimationFrame(this.requestRef);
    this.canvas.removeEventListener('webglcontextlost', this.onContextLost, false);
    this.canvas.removeEventListener('webglcontextrestored', this.onContextRestored, false);

    this.wasm = undefined;
  }
}
